declare global {
  interface Window {
    Locale: string
  }
}

/**
 * Import all polyfills.
 */
import 'url-search-params-polyfill';
import 'custom-event-polyfill';

/**
 * Import all required libraries.
 */
import Vue from 'vue';
import Vuex from 'vuex';
import ServiceContainer from '@flashpointbv/solar-service-container';
import VueI18n from 'vue-i18n';
import VueLazyload from 'vue-lazyload';
import {UICoreSticky, UICoreOffCanvas} from '@flashpointbv/solar-ui-core';
import {SolarDataLayer} from '@flashpointbv/solar-datalayer/dist';

new UICoreSticky().sticky();
new UICoreOffCanvas().offCanvas(true);

Vue.use(VueI18n);
Vue.use(VueLazyload);
Vue.use(Vuex);

SolarDataLayer.init({
  'item_variant': 'color'
});

/*
* Create store instance and register the store modules
 */
import {CheckoutGlobal, CheckoutAddress, CheckoutCart, CheckoutTotals, CheckoutPayment, CheckoutShipping, CheckoutQuote} from '../../default/js/stores/checkout/index';
import CatalogCategoryList from '../../default/js/stores/catalog/category/list';
import CatalogProductView from '../../default/js/stores/catalog/product';
import {CatalogCategory, CatalogProduct, CatalogBundleProduct, CatalogGroupedProduct, CatalogConfigurableProduct} from '@flashpointbv/solar-ui-catalog';


const store = new Vuex.Store({
  strict: false,
});

store.registerModule('CatalogCategory', CatalogCategory);
store.registerModule('CatalogCategoryList', CatalogCategoryList);
store.registerModule('CatalogCategoryProduct', CatalogProductView);
store.registerModule('CatalogProduct', CatalogProduct);
store.registerModule('CatalogBundleProduct', CatalogBundleProduct);
store.registerModule('CatalogGroupedProduct', CatalogGroupedProduct);
store.registerModule('CatalogConfigurableProduct', CatalogConfigurableProduct);

store.registerModule('CheckoutGlobal', CheckoutGlobal);
store.registerModule('CheckoutAddress', CheckoutAddress);
store.registerModule('CheckoutCart', CheckoutCart);
store.registerModule('CheckoutShipping', CheckoutShipping);
store.registerModule('CheckoutPayment', CheckoutPayment);
store.registerModule('CheckoutTotals', CheckoutTotals);
store.registerModule('CheckoutQuote', CheckoutQuote);

/**
 * Require application scripts.
 */
require('../../default/js/prototypes');
require('../../default/js/core');

/**
 * Import all required styles.
 */
import '../scss/app.scss';

/**
 * Register Vue components.
 */
ServiceContainer().addComponents([
  {key: 'search-form', concrete: require('../../default/js/components/SearchForm').default},
  {key: 'header-cart', concrete: require('./components/HeaderCart').default},
  {key: 'footer-subscribe', concrete: require('../../default/js/components/FooterSubscribe').default},
  {key: 'newsletter-widget', concrete: require('./components/NewsletterWidget').default},

  {key: 'home-slider', concrete: require('./components/home/homeSlider').default},
  {key: 'home-list', concrete: require('../../default/js/components/home/HomeList').default},
  {key: 'home-brands', concrete: require('../../default/js/components/home/homeBrands').default},

  //Product configuration
  {key: 'catalog-product-stock', concrete: require('../../default/js/components/catalog/product/ProductStock').default},
  {key: 'catalog-product-configurable', concrete: require('./components/catalog/product/configurable/ProductTypeConfigurable').default},
  {key: 'catalog-product-configuration', concrete: require('../../default/js/components/catalog/product/ProductConfiguration').default},
  {key: 'catalog-product-price', concrete: require('../../default/js/components/catalog/product/ProductPrice').default},
  {key: 'catalog-product-simple', concrete: require('../../default/js/components/catalog/product/simple/ProductTypeSimple').default},
  {key: 'catalog-product-configurable-select', concrete: require('../../default/js/components/catalog/product/configurable/type/OptionSelect').default},
  {key: 'catalog-product-bundle', concrete: require('../../default/js/components/catalog/product/bundle/ProductTypeBundle').default},
  {key: 'catalog-product-bundle-select', concrete: require('../../default/js/components/catalog/product/bundle/type/BundleSelect').default},
  {key: 'catalog-product-bundle-radio', concrete: require('../../default/js/components/catalog/product/bundle/type/BundleRadio').default},
  {key: 'catalog-product-bundle-checkbox', concrete: require('../../default/js/components/catalog/product/bundle/type/BundleCheckbox').default},
  {key: 'catalog-product-grouped', concrete: require('../../default/js/components/catalog/product/grouped/ProductTypeGrouped').default},
  {key: 'catalog-product-grouped-product', concrete: require('../../default/js/components/catalog/product/grouped/ProductTypeGroupedProduct').default},

  {key: 'catalog-product-media', concrete: require('../../default/js/components/catalog/product/ProductMedia').default},
  {key: 'catalog-product-upsell', concrete: require('../../default/js/components/catalog/product/productUpsell').default},

  {key: 'catalog-category-view', concrete: require('../../default/js//views/catalog/CatalogCategoryView').default},
  {key: 'catalog-search-view', concrete: require('../../default/js//views/catalog/CatalogSearchView').default},

  {key: 'catalog-product-list', concrete: require('../../default/js/components/catalog/ProductList').default},
  {key: 'catalog-product-list-item', concrete: require('../../default/js/components/catalog/ProductListItem').default},

  {key: 'sub-category-list', concrete: require('../../default/js/components/catalog/category/SubCategoryList').default},
  {key: 'sub-category-list-item', concrete: require('../../default/js/components/catalog/category/SubCategoryListItem').default},

  {key: 'catalog-product-filter-list', concrete: require('../../default/js/components/catalog/ProductFilterList').default},
  {key: 'attribute-filter', concrete: require('../../default/js/components/catalog/filter/AttributeFilter').default},
  {key: 'price-filter', concrete: require('../../default/js/components/catalog/filter/PriceFilter').default},
  {key: 'sub-categories', concrete: require('../../default/js/components/catalog/filter/SubCategories').default},
  {key: 'catalog-product-active-filter-list', concrete: require('../../default/js/components/catalog/ProductActiveFilterList').default},

  {key: 'catalog-product-list-toolbar', concrete: require('../../default/js/components/catalog/ProductListToolbar').default},
  {key: 'catalog-product-list-toolbar-paginator', concrete: require('../../default/js/components/catalog/ProductListToolbarPaginator').default},
  {key: 'catalog-product-list-toolbar-size-select', concrete: require('../../default/js/components/catalog/ProductListToolbarSizeSelect').default},
  {key: 'catalog-product-list-toolbar-sort-select', concrete: require('../../default/js/components/catalog/ProductListToolbarSortSelect').default},

  {key: 'checkout-checkout', concrete: require('../../default/js/components/checkout/checkout/Checkout').default},

  {key: 'checkout-address-detail', concrete: require('../../default/js/components/checkout/checkout/step/AddressDetail').default},
  {key: 'checkout-address-shipping', concrete: require('../../default/js/components/checkout/checkout/address/ShippingAddress').default},
  {key: 'checkout-address-billing', concrete: require('../../default/js/components/checkout/checkout/address/BillingAddress').default},
  {key: 'checkout-address-login', concrete: require('../../default/js/components/checkout/checkout/address/CheckoutLogin').default},
  {key: 'checkout-address-form', concrete: require('../../default/js/components/checkout/checkout/address/input/AddressForm').default},
  {key: 'checkout-address-select', concrete: require('../../default/js/components/checkout/checkout/address/input/AddressSelect').default},
  {key: 'checkout-address-option', concrete: require('../../default/js/components/checkout/checkout/address/input/AddressOption').default},

  {key: 'checkout-shipping-detail', concrete: require('../../default/js/components/checkout/checkout/step/ShippingDetail').default},
  {key: 'checkout-shipping-methods-default', concrete: require('../../default/js/components/checkout/checkout/shipping/methods/Default').default},
  {key: 'checkout-shipping-postnl-delivery-options', concrete: require('../../default/js/components/checkout/checkout/shipping/PostnlDeliveryOptions').default},

  {key: 'checkout-payment-detail', concrete: require('../../default/js/components/checkout/checkout/step/PaymentDetail').default},
  {key: 'checkout-payment-methods-default', concrete: require('../../default/js/components/checkout/checkout/payment/methods/Default').default},

  {key: 'checkout-summary', concrete: require('../../default/js/components/checkout/checkout/summary/Summary').default},
  {key: 'checkout-coupon-form', concrete: require('../../default/js/components/checkout/coupon/CouponForm').default},
  {key: 'checkout-cart-cross-sell', concrete: require('../../default/js/components/checkout/cart/CartCrossSell').default},
]);

/**
 * Register Vue translations
 */
import nl_NL from '../../default/js/i18n/nl_NL';
import en_GB from '../../default/js/i18n/en_GB';

import { localize } from 'vee-validate';
import en from 'vee-validate/dist/locale/en.json';
import nl from 'vee-validate/dist/locale/nl.json';

localize({
  en,
  nl
});

localize('nl');

const messages = {
  nl_NL,
  en_GB
};

const i18n = new VueI18n({
  locale: window.Locale,
  fallbackLocale: 'nl_NL',
  formatFallbackMessages: true,
  silentTranslationWarn: true,
  messages
});

const stripCheckoutCustomerAddressIds = () => {
  ['checkout-shipping-address', 'checkout-billing-address'].forEach((key) => {
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) {
        return;
      }

      const address = JSON.parse(raw);
      if (!address || typeof address !== 'object') {
        return;
      }

      delete address.id;
      delete address.entity_id;
      delete address.customerAddressId;
      delete address.customer_address_id;
      window.localStorage.setItem(key, JSON.stringify(address));
    } catch (e) {
      // Ignore unreadable checkout address payloads.
    }
  });
};

new Vue({
  store,
  i18n,
  // @ts-ignore
  components: ServiceContainer().getComponents(),
  created() {
    if (window.location.pathname === '/customer/account/logoutSuccess/') {
      stripCheckoutCustomerAddressIds();
    }
  }
}).$mount('#solar-app');
